
import { useState } from 'react';
import { DoctorDashboardData, Notification, Patient, FollowUp } from '@/types/doctor';

// Sample data that would typically come from an API
const sampleDashboardData: DoctorDashboardData = {
  stats: {
    totalPatients: 57,
    activePatients: 45,
    newPatientsThisMonth: 12,
    upcomingAppointments: 8,
    pendingFollowUps: 3,
    revenueThisMonth: 8950,
    newCases: 12,
    newCasesChange: 18,
    acceptedPatients: 45,
    acceptedPatientsChange: 12,
    followUps: 8,
    followUpsChange: 5,
    incomeThisMonth: 8950,
    incomeChange: 15,
    completedTreatments: 0
  },
  newCases: [
    {
      id: "1",
      name: 'Emily Johnson',
      condition: 'Dental Implant Consultation',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&auto=format&fit=crop',
      date: '2025-04-15',
      status: 'pending',
      treatmentPlans: [
        { id: 101, version: 1, status: 'pending', date: '2025-04-15' }
      ]
    },
    {
      id: "2",
      name: 'Michael Chen',
      condition: 'Teeth Whitening & Cleaning',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&auto=format&fit=crop',
      date: '2025-04-16',
      status: 'pending',
      treatmentPlans: [
        { id: 102, version: 1, status: 'pending', date: '2025-04-16' }
      ]
    },
    {
      id: "3",
      name: 'Sarah Williams',
      condition: 'Crown Replacement',
      avatar: null,
      date: '2025-04-17',
      status: 'pending',
      treatmentPlans: [
        { id: 103, version: 1, status: 'pending', date: '2025-04-17' }
      ]
    },
  ],
  acceptedPatients: [
    {
      id: "4",
      name: 'David Rodriguez',
      condition: 'Full Mouth Reconstruction',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&h=120&auto=format&fit=crop',
      date: '2025-04-20',
      status: 'in-progress',
      progress: 65,
      treatmentPlans: [
        { id: 104, version: 1, status: 'accepted', date: '2025-04-20' }
      ]
    },
    {
      id: "5",
      name: 'Jennifer Lee',
      condition: 'Veneers (8 units)',
      avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=120&h=120&auto=format&fit=crop',
      date: '2025-04-22',
      status: 'in-progress',
      progress: 30,
      treatmentPlans: [
        { id: 105, version: 1, status: 'accepted', date: '2025-04-22' }
      ]
    },
  ],
  followUps: [
    {
      id: "1",
      patientId: "1",
      patientName: 'Robert Smith',
      date: '2025-04-16',
      treatment: 'Implant Follow-up',
      status: 'scheduled',
    },
    {
      id: "2",
      patientId: "2",
      patientName: 'Anna Thompson',
      date: '2025-04-18',
      treatment: 'Crown Check',
      status: 'scheduled',
    },
    {
      id: "3",
      patientId: "3",
      patientName: 'Maria Garcia',
      date: '2025-04-10',
      treatment: 'Post-op Check',
      status: 'completed',
    },
  ],
  notifications: [
    {
      id: "1",
      type: 'case',
      title: 'New Case Submission',
      description: 'Emily Johnson has submitted a new case for dental implants',
      message: 'Emily Johnson has submitted a new case for dental implants',
      read: false,
      isRead: false,
      date: '2025-04-15',
      time: '2 hours ago',
      link: '/patient/1',
    },
    {
      id: "2",
      type: 'appointment',
      title: 'Appointment Reminder',
      description: 'You have a follow-up with Robert Smith tomorrow at 10:00 AM',
      message: 'You have a follow-up with Robert Smith tomorrow at 10:00 AM',
      isRead: false,
      read: false,
      date: '2025-04-15',
      time: '6 hours ago',
      link: '/appointments/2',
    },
    {
      id: "3",
      type: 'patient',
      title: 'Treatment Plan Accepted',
      description: 'Jennifer Lee has accepted the treatment plan for veneers',
      message: 'Jennifer Lee has accepted the treatment plan for veneers',
      isRead: false,
      read: false,
      date: '2025-04-14',
      time: '1 day ago',
      link: '/treatment-plan/3',
    },
    {
      id: "4",
      type: 'payment',
      title: 'Payment Received',
      description: 'You received a payment of $1,500 from David Rodriguez',
      message: 'You received a payment of $1,500 from David Rodriguez',
      isRead: false,
      read: false,
      date: '2025-04-13',
      time: '2 days ago',
      link: '/finances/4',
    },
    {
      id: "5",
      type: 'message',
      title: 'New Message',
      description: 'You have a new message from Anna Thompson regarding her appointment',
      message: 'You have a new message from Anna Thompson regarding her appointment',
      isRead: false,
      read: false,
      date: '2025-04-12',
      time: '3 days ago',
      link: '/messages/5',
    },
    {
      id: "6",
      type: 'score-warning',
      title: 'Post-Treatment Report Needed',
      description: 'Alex Johnson completed treatment. Post-treatment report needed.',
      message: 'Alex Johnson completed treatment. Post-treatment report needed.',
      isRead: false,
      read: false,
      date: '2025-04-14',
      time: '1 day ago',
      link: '/post-treatment-report/6',
    },
  ],
  adStats: {
    views: 1245,
    newPatients: 8,
    spent: 350,
    activeAds: 3
  },
  patients: [],
  recentPatients: [],
  upcomingAppointments: [], 
  recentActivity: []
};

export const useDoctorDashboard = () => {
  const [dashboardData] = useState<DoctorDashboardData>(sampleDashboardData);
  const [isLoading, setIsLoading] = useState(false);
  
  const refetch = () => {
    // In a real app, this would fetch updated data from an API
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };
  
  return {
    doctorDashboardData: dashboardData,
    refetch,
    isLoading
  };
};
